import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { contentApi } from '../api';
import type { ApiContent, ContentResponse, ContentQuery } from '../api/types';
import { useCustomQuery } from './useCustomQuery';

export const contentQueryKeys = {
  all: ['content'] as const,
  detail: (id: number | string) => ['content', id] as const,
} as const;

// Hook to fetch content list with infinite scroll
export function useContentList(query?: ContentQuery, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: [...contentQueryKeys.all, query],
    queryFn: async (ctx) => {
      const cursor = ctx.pageParam as string;
      const response: ContentResponse = await contentApi.getContent({
        ...query,
        nextCursor: cursor || undefined,
      });
      return response;
    },
    staleTime: 1000 * 10,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor ?? undefined,
    initialPageParam: '',
    enabled: options?.enabled,
  });
}

// Hook to fetch a single content item by ID or slug
export function useContentItem(
  id: number | string,
  options?: { enabled?: boolean; refetchOnMount?: boolean | 'always' }
) {
  return useCustomQuery({
    queryKey: contentQueryKeys.detail(id),
    queryFn: () => contentApi.getContentItem(id),
    enabled: options?.enabled !== false && !!id,
    staleTime: 1000 * 10,
    // Detail-only fields (e.g. `following`) are absent from list responses used
    // to seed the cache. Callers rendering those fields can force a fresh fetch.
    refetchOnMount: options?.refetchOnMount,
  });
}

// Hook to create content with optimistic updates
export function useCreateContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof contentApi.createContent>[0]) =>
      contentApi.createContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentQueryKeys.all });
    },
  });
}

// Hook to update content with optimistic updates
export function useUpdateContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Parameters<typeof contentApi.updateContent>[1];
    }) => contentApi.updateContent(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: contentQueryKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: contentQueryKeys.all });

      const previousDetail = queryClient.getQueryData(contentQueryKeys.detail(id));
      const previousList = queryClient.getQueriesData({ queryKey: contentQueryKeys.all });

      // Optimistic update on detail
      queryClient.setQueryData(contentQueryKeys.detail(id), (old: ApiContent | undefined) => {
        if (!old) return old;
        return { ...old, ...data };
      });

      // Optimistic update on list pages
      queryClient.setQueriesData({ queryKey: contentQueryKeys.all }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: ContentResponse) => ({
            ...page,
            content: page.content.map((item: ApiContent) =>
              item.id === id ? { ...item, ...data } : item
            ),
          })),
        };
      });

      return { previousDetail, previousList };
    },
    onSuccess: (updated, { id }) => {
      queryClient.setQueryData(contentQueryKeys.detail(id), updated);
      queryClient.invalidateQueries({ queryKey: contentQueryKeys.all });
    },
    onError: (_err, { id }, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(contentQueryKeys.detail(id), context.previousDetail);
      }
      if (context?.previousList) {
        context.previousList.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}
